import Bugsnag from "@bugsnag/js";
import { supabase } from "@/lib/supabase";
import type { UserData } from "@/types/user-data";

type ResolveOrgIdsRow = { district_id: string | null; school_id: string | null };

function notify(error: unknown, context: string): void {
  Bugsnag.notify(error instanceof Error ? error : new Error(String(error)), {
    severity: "warning",
    context,
  });
}

async function resolveOrgIds(districtName: string, schoolName: string | null): Promise<ResolveOrgIdsRow | null> {
  const { data, error } = await supabase.rpc("resolve_org_ids_by_name", {
    _district_name: districtName,
    _school_name: schoolName,
  });

  if (error) {
    notify(new Error(error.message ?? "resolve_org_ids_by_name failed"), "accountOrgRequestService.resolveOrgIds");
    return null;
  }

  const rows = data as ResolveOrgIdsRow[] | ResolveOrgIdsRow | null;
  return (Array.isArray(rows) ? rows[0] : rows) ?? null;
}

async function linkProfileOrg(
  userId: string,
  districtId: string,
  schoolId: string | null,
  userName: string
): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .update({ district_id: districtId, school_id: schoolId, user_name: userName })
    .eq("id", userId);

  if (error) {
    notify(new Error(error.message ?? "profiles update failed"), "accountOrgRequestService.linkProfileOrg");
  }
}

async function submitAccountOrgRequest(input: {
  email: string;
  displayName: string;
  districtName: string | null;
  customDistrictName: string | null;
  schoolName: string | null;
  customSchoolName: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc("submit_account_org_request", {
    _email: input.email,
    _display_name: input.displayName,
    _district_name: input.districtName,
    _custom_district_name: input.customDistrictName,
    _school_name: input.schoolName,
    _custom_school_name: input.customSchoolName,
  });

  if (error) {
    notify(
      new Error(error.message ?? "submit_account_org_request failed"),
      "accountOrgRequestService.submitAccountOrgRequest"
    );
  }
}

/**
 * Best-effort post-onboarding org linkage (AE-199). Runs after the `users`
 * row is already saved, so any failure here is reported to Bugsnag rather
 * than surfaced to the user — the profile save itself already succeeded.
 */
export async function syncAccountOrgLinkage(profileData: Omit<UserData, "created_at">): Promise<void> {
  const isCustomDistrict = !profileData.district_name && Boolean(profileData.custom_district_name);
  const isCustomSchool = !profileData.school_name && Boolean(profileData.custom_school_name);

  try {
    if (!isCustomDistrict && profileData.district_name) {
      const resolved = await resolveOrgIds(profileData.district_name, isCustomSchool ? null : profileData.school_name ?? null);
      if (resolved?.district_id) {
        await linkProfileOrg(
          profileData.user_id,
          resolved.district_id,
          isCustomSchool ? null : resolved.school_id,
          profileData.user_name
        );
      }
    }

    if (isCustomDistrict || isCustomSchool) {
      await submitAccountOrgRequest({
        email: profileData.email,
        displayName: profileData.user_name,
        districtName: profileData.district_name ?? null,
        customDistrictName: profileData.custom_district_name ?? null,
        schoolName: profileData.school_name ?? null,
        customSchoolName: profileData.custom_school_name ?? null,
      });
    }
  } catch (error) {
    notify(error, "accountOrgRequestService.syncAccountOrgLinkage");
  }
}
